import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Input } from '@/components/ui/Field';
import { Banner, ErrorState, Skeleton } from '@/components/ui/Feedback';
import { PageHeader } from '@/app/PlaceholderPage';
import { ApiError } from '@/lib/api';
import { cn } from '@/lib/cn';
import {
  CHILD_LEVEL,
  LEVEL_LABEL,
  settingsApi,
  type Region,
  type RegionLevel,
} from './api';

type EditTarget =
  | { mode: 'create'; parentId: string | null; level: RegionLevel; parentName: string }
  | { mode: 'edit'; region: Region }
  | null;

/** L-14 — Kelola wilayah berjenjang: Provinsi → Kabupaten → Desa. */
export function RegionsPage() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<EditTarget>(null);
  const [deleting, setDeleting] = useState<Region | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);

  const { data, isLoading, error: loadError, refetch } = useQuery({
    queryKey: ['regions', 'all'],
    queryFn: () => settingsApi.listRegions(),
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['regions'] });

  const save = useMutation({
    mutationFn: (name: string) => {
      if (!editing) throw new Error('no target');
      return editing.mode === 'edit'
        ? settingsApi.updateRegion(editing.region.id, { name })
        : settingsApi.createRegion({ parentId: editing.parentId, level: editing.level, name });
    },
    onSuccess: () => {
      refresh();
      setEditing(null);
      setError(null);
      setBanner('Wilayah tersimpan.');
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : 'Gagal menyimpan wilayah.'),
  });

  const remove = useMutation({
    mutationFn: () => settingsApi.deleteRegion(deleting!.id),
    onSuccess: () => {
      refresh();
      setDeleting(null);
      setBanner('Wilayah dihapus.');
    },
    onError: (err) => {
      setDeleting(null);
      setError(err instanceof ApiError ? err.message : 'Gagal menghapus wilayah.');
    },
  });

  const all = data ?? [];
  const childrenOf = (parentId: string | null) =>
    all
      .filter((r) => r.parentId === parentId)
      .sort((a, b) => a.name.localeCompare(b.name, 'id'));

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Wilayah"
        description="Daftar acuan lokasi TBM. Provinsi → Kabupaten → Desa/Nagari."
        action={
          <Button
            onClick={() =>
              setEditing({ mode: 'create', parentId: null, level: 'province', parentName: '' })
            }
          >
            + Provinsi
          </Button>
        }
      />

      {banner && <Banner tone="success">{banner}</Banner>}
      {error && <Banner tone="danger">{error}</Banner>}

      {loadError ? (
        <ErrorState message={(loadError as Error).message} onRetry={() => refetch()} />
      ) : isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : all.length === 0 ? (
        <Card>
          <p className="text-ink-secondary">Belum ada wilayah. Mulai dengan menambah provinsi.</p>
        </Card>
      ) : (
        <div className="flex flex-col gap-3">
          {childrenOf(null).map((province) => (
            <RegionNode
              key={province.id}
              region={province}
              childrenOf={childrenOf}
              onAddChild={(parent) => {
                const level = CHILD_LEVEL[parent.level];
                if (level) {
                  setEditing({
                    mode: 'create',
                    parentId: parent.id,
                    level,
                    parentName: parent.name,
                  });
                }
              }}
              onEdit={(region) => setEditing({ mode: 'edit', region })}
              onDelete={setDeleting}
            />
          ))}
        </div>
      )}

      <Card>
        <p className="text-[13px] text-ink-secondary">
          Wilayah adalah data acuan, bukan teks bebas — itulah yang menjaga "Kab Solok",
          "Kabupaten Solok", dan "solok" tetap terbaca sebagai satu wilayah, sehingga agregasi
          dashboard tidak pecah. Karena itu tingkat dan induknya tidak bisa diubah setelah dibuat:
          memindahkan desa ke kabupaten lain akan diam-diam memindahkan seluruh TBM di dalamnya.
        </p>
      </Card>

      {editing && (
        <RegionEditor
          target={editing}
          saving={save.isPending}
          error={error}
          onCancel={() => {
            setEditing(null);
            setError(null);
          }}
          onSave={(name) => save.mutate(name)}
        />
      )}

      <ConfirmDialog
        open={deleting !== null}
        title={`Hapus ${deleting ? LEVEL_LABEL[deleting.level] : ''}?`}
        confirmLabel="Ya, hapus"
        loading={remove.isPending}
        onCancel={() => setDeleting(null)}
        onConfirm={() => remove.mutate()}
      >
        <p className="mb-3 rounded-[var(--radius-input)] bg-app px-3.5 py-2.5 font-medium text-ink">
          {deleting?.name}
        </p>
        <p>
          Penghapusan akan ditolak kalau wilayah ini masih memayungi wilayah lain atau masih
          dipakai TBM — sistem akan menyebutkan apa yang menahannya.
        </p>
      </ConfirmDialog>
    </div>
  );
}

function RegionNode({
  region,
  childrenOf,
  onAddChild,
  onEdit,
  onDelete,
  depth = 0,
}: {
  region: Region;
  childrenOf: (parentId: string | null) => Region[];
  onAddChild: (parent: Region) => void;
  onEdit: (region: Region) => void;
  onDelete: (region: Region) => void;
  depth?: number;
}) {
  const [open, setOpen] = useState(depth === 0);
  const children = childrenOf(region.id);
  const childLevel = CHILD_LEVEL[region.level];

  return (
    <Card padded={false} className={cn('min-w-0', depth > 0 && 'shadow-none')}>
      <div className="flex flex-wrap items-center gap-2 px-4 py-3">
        <button
          onClick={() => setOpen((v) => !v)}
          className="flex min-h-11 min-w-0 flex-1 items-center gap-2 text-left"
          aria-expanded={open}
          disabled={!childLevel}
        >
          {childLevel ? (
            <span className="w-4 text-ink-muted" aria-hidden>
              {open ? '▾' : '▸'}
            </span>
          ) : (
            <span className="w-4" aria-hidden />
          )}
          <span className="min-w-0">
            <span className="font-medium text-ink">{region.name}</span>
            <span className="ml-2 text-[13px] text-ink-secondary">
              {LEVEL_LABEL[region.level]}
              {childLevel && ` · ${children.length} ${LEVEL_LABEL[childLevel].toLowerCase()}`}
            </span>
          </span>
        </button>

        <div className="flex flex-wrap gap-1">
          {childLevel && (
            <Button size="sm" variant="ghost" onClick={() => onAddChild(region)}>
              + {LEVEL_LABEL[childLevel]}
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={() => onEdit(region)}>
            Ubah
          </Button>
          <Button size="sm" variant="ghost" onClick={() => onDelete(region)}>
            Hapus
          </Button>
        </div>
      </div>

      {open && children.length > 0 && (
        <div className="flex flex-col gap-2 border-t border-border bg-app px-3 py-3">
          {children.map((child) => (
            <RegionNode
              key={child.id}
              region={child}
              childrenOf={childrenOf}
              onAddChild={onAddChild}
              onEdit={onEdit}
              onDelete={onDelete}
              depth={depth + 1}
            />
          ))}
        </div>
      )}

      {open && childLevel && children.length === 0 && (
        <p className="border-t border-border bg-app px-5 py-3 text-[13px] text-ink-secondary">
          Belum ada {LEVEL_LABEL[childLevel].toLowerCase()} di sini.
        </p>
      )}
    </Card>
  );
}

function RegionEditor({
  target,
  saving,
  error,
  onCancel,
  onSave,
}: {
  target: NonNullable<EditTarget>;
  saving: boolean;
  error: string | null;
  onCancel: () => void;
  onSave: (name: string) => void;
}) {
  const [name, setName] = useState(target.mode === 'edit' ? target.region.name : '');
  const [fieldError, setFieldError] = useState('');

  const level = target.mode === 'edit' ? target.region.level : target.level;

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) {
      setFieldError('Nama wilayah wajib diisi');
      return;
    }
    onSave(name.trim());
  }

  return (
    <form
      onSubmit={submit}
      noValidate
      className="fixed inset-0 z-50 flex items-end justify-center bg-ink/40 p-0 sm:items-center sm:p-4"
    >
      <div className="w-full max-w-md rounded-t-[var(--radius-card)] bg-surface p-5 shadow-[var(--shadow-hover)] sm:rounded-[var(--radius-card)]">
        <h2 className="text-[19px] font-semibold text-ink">
          {target.mode === 'edit' ? `Ubah ${LEVEL_LABEL[level]}` : `${LEVEL_LABEL[level]} Baru`}
        </h2>
        {target.mode === 'create' && target.parentName && (
          <p className="mt-1 text-[13px] text-ink-secondary">Di dalam {target.parentName}</p>
        )}

        {error && (
          <div className="mt-3">
            <Banner tone="danger">{error}</Banner>
          </div>
        )}

        <div className="mt-4">
          <Input
            label={`Nama ${LEVEL_LABEL[level]}`}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setFieldError('');
            }}
            error={fieldError}
            hint={level === 'village' ? 'Mis. Nagari Paninjawan, Desa Cijayanti' : undefined}
            required
          />
        </div>

        <div className="mt-5 flex flex-col-reverse gap-2.5 sm:flex-row sm:justify-end">
          <Button type="button" variant="secondary" onClick={onCancel} disabled={saving}>
            Batal
          </Button>
          <Button type="submit" loading={saving}>
            Simpan
          </Button>
        </div>
      </div>
    </form>
  );
}
